import { useEffect, useMemo } from "react";

import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { isIssueAgeStatus, type IssueAgeStatus, type IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { formatAge } from "#app/lib/issue-utils";
import { useMinuteKey } from "#app/lib/hooks/use-date-boundary-key";
import {
  clockRunning,
  formatDueLine,
  liveAgeStatus,
  nextUpdateDue,
} from "#app/lib/project-updates";
import { useEntityList } from "../entity-list/use-entity-list";
import { AUTO_PAGE_ROW_CAP, useIssueRowsPaged } from "../shared/use-issue-rows-paged";

/**
 * The three tiers of the one deadline vocabulary, in the order a queue reads
 * them: most owed first. The keys are the server's age statuses, so the band
 * copy, the chip hue and the filter option all come off `AGE_STATUS_CONFIG`.
 */
export type FollowUpTier = IssueAgeStatus;

export const FOLLOW_UP_TIERS: readonly FollowUpTier[] = [
  AGE_STATUS.offTrack,
  AGE_STATUS.atRisk,
  AGE_STATUS.onTrack,
];

export type FollowUpQueueRow = {
  id: string;
  name: string;
  tier: FollowUpTier;
  /** The deadline as an epoch instant — what the rows inside a band sort on.
   *  `Infinity` when a running clock has no resolvable deadline. */
  dueAt: number;
  /** The deadline as the row's trailing line reads it: "3 days overdue",
   *  "Due in 5 hours", "Due today". */
  deadline: string;
  issue: IssueRow;
};

export type FollowUpBand = {
  tier: FollowUpTier;
  label: string;
  rows: FollowUpQueueRow[];
};

/**
 * The queue's shape: every tier stands as a band whether or not a row landed
 * in it, most owed first, and inside a band the nearest deadline leads — the
 * most overdue Project, then the one that ran out an hour ago. Ties fall to
 * the name so the order is stable between renders.
 */
export function bucketFollowUps(rows: readonly FollowUpQueueRow[]): FollowUpBand[] {
  const byTier = new Map<FollowUpTier, FollowUpQueueRow[]>(
    FOLLOW_UP_TIERS.map((tier) => [tier, []]),
  );
  for (const row of rows) byTier.get(row.tier)?.push(row);
  return FOLLOW_UP_TIERS.map((tier) => ({
    tier,
    label: AGE_STATUS_CONFIG[tier].label,
    rows: [...(byTier.get(tier) ?? [])].sort(
      (a, b) => a.dueAt - b.dueAt || a.name.localeCompare(b.name),
    ),
  }));
}

/**
 * The Projects with a running clock, whatever tier the stored status says:
 * the tier is derived LIVE below, the way the chip on the row derives it, so
 * the band a row sits under and the hue it wears can never disagree. The
 * stored column moves only on the hourly cron pass, so a row's live tier and
 * its stored status can differ by up to an hour.
 */
const RUNNING_CLOCK_FILTERS = {
  // A queue of what is owed NOW: a board's date window would narrow on the
  // last update and drop exactly the Stale rows that have not been updated.
  dateRange: { key: "all-time" },
  query: {
    logic: "AND",
    blocks: [
      {
        logic: "AND",
        filters: [
          {
            field: "age.status",
            operator: "in",
            value: [AGE_STATUS.onTrack, AGE_STATUS.atRisk, AGE_STATUS.offTrack],
          },
        ],
      },
    ],
  },
} as const;

/** One page holds a Member's whole clocked set in one round trip; the tail
 *  effect pages on only past that, up to `AUTO_PAGE_ROW_CAP`. */
const PAGE_SIZE = 200;

/**
 * Everything the Follow ups card renders: a Member's Projects with a running
 * Follow up clock as queue rows, banded Stale / Update soon / On track. Tier
 * and deadline are derived from the last Update and the effective cycle
 * (`liveAgeStatus` / `nextUpdateDue`). Rows whose Workflow turned updates off
 * keep a stale status on the server and drop out here (`clockRunning`), as
 * they do from every chip. `owed` is what the card exists for — the Stale and
 * Update soon rows — and `total` is what the title wears.
 */
export function useFollowUpList(): {
  bands: FollowUpBand[];
  /** Every distinct age string the queue's rows wear ("20d", "Today",
   *  "3mo"), folded bands included — the row's age gutter sizes itself to
   *  the widest of them, so one column fits whatever the data holds and no
   *  name shifts when a band unfolds or a count grows a digit. */
  ageSizers: readonly string[];
  total: number;
  owed: number;
  loading: boolean;
} {
  const { where, orderBy } = useEntityList("age", RUNNING_CLOCK_FILTERS, "");
  const query = useMemo(() => ({ where, orderBy }), [where, orderBy]);
  const { rows: issues, loading, loadingMore, hasMore, loadMore } = useIssueRowsPaged(query, {
    pageSize: PAGE_SIZE,
  });
  // Every clocked row is needed to band the queue, so the pages follow one
  // another as they land rather than waiting on a scroll.
  useEffect(() => {
    if (hasMore && !loading && !loadingMore && issues.length < AUTO_PAGE_ROW_CAP) loadMore();
  }, [hasMore, loading, loadingMore, issues.length, loadMore]);

  const { stageMap, workflowMap } = useWorkspaceData();
  // Tiers and deadlines move with the clock; a card left open re-buckets.
  const minute = useMinuteKey();
  const rows = useMemo(() => {
    void minute;
    const now = new Date();
    const out: FollowUpQueueRow[] = [];
    for (const issue of issues) {
      const stage = stageMap.get(issue.stageId);
      const workflow = stage?.workflowId ? workflowMap.get(stage.workflowId) : undefined;
      if (!clockRunning(issue.age, stage, workflow)) continue;
      const live = liveAgeStatus(issue.age, stage, workflow, now);
      const stored = issue.age?.status;
      const tier = live ?? (isIssueAgeStatus(stored) ? stored : null);
      if (!tier) continue;
      const deadline = nextUpdateDue(issue.age, stage, workflow);
      out.push({
        id: issue.issueId,
        name: issue.name,
        tier,
        dueAt: deadline ? deadline.getTime() : Number.POSITIVE_INFINITY,
        deadline: deadline ? formatDueLine(deadline, now) : "",
        issue,
      });
    }
    return out;
  }, [issues, stageMap, workflowMap, minute]);

  const bands = useMemo(() => bucketFollowUps(rows), [rows]);
  // One sizer per SHAPE of age string, not per string: the gutter's digits
  // are tabular, so "12d" and "00d" are one width, and a queue of any length
  // needs only a few sizers ("Today", "0d", "00d", "0mo"…) in each row.
  // Follows `rows`, which already re-derives each minute, so a count that
  // grows a digit at midnight widens the gutter with it.
  const sizerKey = useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      const age = formatAge(row.issue.age?.date);
      if (age) seen.add(age.replace(/\d/g, "0"));
    }
    return [...seen].sort().join("|");
  }, [rows]);
  const ageSizers = useMemo(() => (sizerKey ? sizerKey.split("|") : []), [sizerKey]);
  const owed = bands
    .filter((band) => band.tier !== AGE_STATUS.onTrack)
    .reduce((sum, band) => sum + band.rows.length, 0);

  return { bands, ageSizers, total: rows.length, owed, loading: loading && issues.length === 0 };
}
